import type { ReactNode } from 'react';
import Link from 'next/link';
import { FiHelpCircle } from 'react-icons/fi';

import type { StackProps } from '@highlight-cp/core-ui';
import { Button, Flex, Stack, Text, Title, Tooltip } from '@highlight-cp/core-ui';

interface FeatureHeaderProps extends Partial<StackProps> {
  title: string;
  description: ReactNode;
  helpTooltip?: string;
  helpLink?: string;
  onHelpClick?: () => void;
}

/**
 * ### FeatureHeader
 *
 * @description
 * A header component for feature sections that displays a title and description as well as optional help links
 *
 * @example
 * <FeatureHeader
 *   title="Feature Name"
 *   description="Description of the feature functionality"
 * />
 *
 * @param props - Component props
 * @returns React component
 */
const FeatureHeader = ({
  title,
  description,
  helpTooltip,
  helpLink,
  onHelpClick,
  ...stackProps
}: FeatureHeaderProps) => {
  const helpTooltipText = helpTooltip || `Click to learn more about ${title}`;
  const sharedButtonProps = {
    'aria-label': helpTooltipText,
    variant: 'subtle',
    h: 'auto',
    p: 0,
  } as const;

  return (
    <Stack gap={4} {...stackProps}>
      <Flex gap="sm" align="center">
        <Title order={2}>{title}</Title>
        {(helpLink || onHelpClick) && (
          <Tooltip label={helpTooltipText}>
            {onHelpClick ? (
              <Button {...sharedButtonProps} onClick={onHelpClick}>
                <FiHelpCircle size={24} />
              </Button>
            ) : (
              <Button {...sharedButtonProps} component={Link} href={helpLink ?? ''} target="_blank">
                <FiHelpCircle size={24} />
              </Button>
            )}
          </Tooltip>
        )}
      </Flex>
      <Text>{description}</Text>
    </Stack>
  );
};

export default FeatureHeader;
